// Versão: 3.05
// Data..: 2026-10-15
// v3.05: exportação PDF de horas de suporte passa r.tickets (chamados atendidos) para o
// detalhamento por empresa no PDF; a tela permanece inalterada.
// v3.04: horas de suporte exibidas em HH:MM (antes decimais) e exportação trocada de
// CSV para PDF (generateSupportHoursPdf, com coluna de horas em HH:MM e totais).
// v3.03: distingue ERRO de VAZIO na seção "Horas de suporte por empresa" — falha ao
// carregar /billing mostra bloco de alerta (em vez de "Sem horas no período").
// v3.02: seção ADMIN-only "Horas de suporte por empresa (contrato)" — tabela com
// horas contratadas vs gastas (faturáveis), excedente e valor adicional (R$),
// seletor De/Até (default mês corrente), destaque de excedente e export CSV.
// Consome GET /billing (ADMIN-only no backend); não busca/exibe para não-admin.
// v3.01: indicadores clicáveis (drill-down) — KPIs e gráficos de status/prioridade
// levam à lista de chamados filtrada (/tickets?status=...&priority=...).
// v3.00: card "Chamados por empresa" (ticketsByCompany) + filtro multi-seleção de
// painéis (MultiSelect) para escolher quais dados/gráficos exibir. Seleção persistida.
// v2.01: página em .full-bleed (ocupa toda a largura do painel, sem limite de 1600px).
// Painel gerencial (staff): SLA, tempos médios, backlog, ranking e horas por empresa.
// v2.00: KPIs coloridos, gráficos com cores por recurso e exportação em PDF (client-side).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusLabels, PriorityLabels, TicketStatus, UserRole, type ManagementMetrics, type BillingReport } from '@makweb/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { BarChart } from '../components/BarChart.js';
import { MultiSelect } from '../components/MultiSelect.js';
import { generateManagementPdf, generateSupportHoursPdf } from '../lib/pdfReport.js';

// Formata minutos em "Xh Ymin" ou "Ymin".
function fmtMin(min: number): string {
  if (!min) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m ? ` ${m}min` : ''}` : `${m}min`;
}

// Formata minutos como "HH:MM" (zero-padded). Ex.: 750 min -> "12:30". Usado nas
// colunas de horas do relatório de suporte (mais claro que horas decimais).
const fmtHM = (min: number): string => {
  const total = Math.max(0, Math.round(min));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// Formata um valor em R$ (pt-BR). undefined/null → "—".
const fmtMoney = (v?: number): string =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Cores por status e prioridade (consistentes com os badges do tema).
const STATUS_COLORS: Record<string, string> = {
  open: 'var(--info)', triage: 'var(--purple)', in_progress: 'var(--brand)',
  waiting_client: 'var(--warning)', waiting_third_party: 'var(--warning)',
  scheduled: 'var(--purple)', resolved: 'var(--success)', closed: 'var(--text-3)',
};
const PRIORITY_COLORS: Record<string, string> = {
  low: 'var(--text-3)', normal: 'var(--info)', high: 'var(--warning)', urgent: 'var(--danger)',
};
// Cores rotativas para dimensões sem cor semântica (categoria, técnico, empresa).
const PALETTE = ['var(--brand)', 'var(--purple)', 'var(--info)', 'var(--success)', 'var(--warning)', '#e0559f', '#0ea5a5', '#f97316'];
const withPalette = (data: { label: string; value: number }[]) =>
  data.map((d, i) => ({ ...d, color: PALETTE[i % PALETTE.length] }));

// Painéis disponíveis no filtro. A ordem aqui define a ordem de exibição.
const PANELS = [
  { id: 'sla', label: 'SLA de resolução' },
  { id: 'status', label: 'Chamados por status' },
  { id: 'priority', label: 'Chamados por prioridade' },
  { id: 'ticketsByCompany', label: 'Chamados por empresa' },
  { id: 'backlog', label: 'Backlog por categoria' },
  { id: 'technicians', label: 'Ranking de técnicos' },
  { id: 'hoursByCompany', label: 'Horas por empresa' },
  { id: 'supportHours', label: 'Horas de suporte (contrato)' },
] as const;
type PanelId = (typeof PANELS)[number]['id'];
const ALL_PANEL_IDS = PANELS.map((p) => p.id) as PanelId[];

// Chave de persistência da seleção de painéis.
const PANELS_KEY = 'makweb.management.panels';

export function ManagementPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  // A seção "Horas de suporte (contrato)" é ADMIN-only: a rota /billing já é
  // ADMIN-only no backend; aqui controlamos a UI (não exibir nem buscar p/ não-admin).
  const isAdmin = user?.role === UserRole.ADMIN;
  const [m, setM] = useState<ManagementMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  // Relatório de horas de suporte por empresa (mês corrente por default). Só admin.
  const firstOfMonth = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); })();
  const today = new Date().toISOString().slice(0, 10);
  const [billing, setBilling] = useState<BillingReport | null>(null);
  // Erro ao carregar o relatório de horas de suporte (distingue falha de vazio legítimo).
  const [billingError, setBillingError] = useState<string | null>(null);
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);

  // Painéis visíveis (multi-seleção). Persistido no navegador; default = todos.
  const [visible, setVisible] = useState<PanelId[]>(() => {
    try {
      const raw = localStorage.getItem(PANELS_KEY);
      if (!raw) return ALL_PANEL_IDS;
      const saved = JSON.parse(raw) as string[];
      const filtered = ALL_PANEL_IDS.filter((id) => saved.includes(id));
      return filtered.length ? filtered : ALL_PANEL_IDS;
    } catch {
      return ALL_PANEL_IDS;
    }
  });
  const setPanels = (ids: PanelId[]) => {
    setVisible(ids);
    try { localStorage.setItem(PANELS_KEY, JSON.stringify(ids)); } catch { /* ignora */ }
  };
  // supportHours é admin-only: nunca "mostra" para não-admin, mesmo se persistido.
  const shows = (id: PanelId) => visible.includes(id) && (id !== 'supportHours' || isAdmin);

  useEffect(() => {
    api.get<ManagementMetrics>('/metrics').then(setM).catch(() => setM(null)).finally(() => setLoading(false));
  }, []);

  // Busca o relatório de horas de suporte SOMENTE para admin (nunca p/ não-admin).
  useEffect(() => {
    if (!isAdmin) { setBilling(null); return; }
    setBillingError(null);
    const params = new URLSearchParams();
    if (from) params.set('from', `${from}T00:00:00.000Z`);
    if (to) params.set('to', `${to}T23:59:59.999Z`);
    api
      .get<BillingReport>(`/billing?${params.toString()}`)
      .then(setBilling)
      .catch((err) => {
        console.error('Falha ao carregar /billing', err);
        setBilling(null);
        setBillingError('Falha ao carregar o relatório de horas de suporte.');
      });
  }, [isAdmin, from, to]);

  // Exporta o relatório de horas de suporte em PDF (horas em HH:MM + valor adicional).
  const exportSupportPdf = () => {
    if (!billing) return;
    generateSupportHoursPdf({
      from,
      to,
      rows: billing.rows.map((r) => ({
        companyName: r.companyName,
        contractedMinutes: r.contractedMinutes,
        billableMinutes: r.billableMinutes,
        overageMinutes: r.overageMinutes,
        overageAmount: r.overageAmount,
        // Detalhamento dos chamados atendidos (só usado no PDF; a tela não muda).
        tickets: r.tickets?.map((t) => ({
          ticketNumber: t.ticketNumber,
          subject: t.subject,
          performedAt: t.performedAt,
          userName: t.userName,
          minutes: t.minutes,
          billable: t.billable,
          description: t.description,
        })),
      })),
    });
  };

  if (loading) return <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>;
  if (!m) return <div className="card empty"><div className="empty-icon">🔒</div>Disponível apenas para a equipe.</div>;

  const statusData = Object.entries(m.byStatus).map(([k, v]) => ({ label: StatusLabels[k] ?? k, value: v, color: STATUS_COLORS[k] ?? 'var(--brand)', key: k }));
  const priorityData = Object.entries(m.byPriority).map(([k, v]) => ({ label: PriorityLabels[k] ?? k, value: v, color: PRIORITY_COLORS[k], key: k }));

  // Navega para a lista de chamados aplicando um filtro (drill-down do painel).
  const goTickets = (params: Record<string, string>) => navigate(`/tickets?${new URLSearchParams(params).toString()}`);

  const exportPdf = () => {
    try {
      generateManagementPdf({
        metrics: m,
        statusData: statusData.map((d) => ({ label: d.label, value: d.value })),
        priorityData: priorityData.map((d) => ({ label: d.label, value: d.value })),
        fmtMin,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao gerar PDF');
    }
  };

  return (
    <div className="full-bleed">
      <PageHeader
        eyebrow="Gestão"
        title="Painel gerencial"
        description="Indicadores operacionais do atendimento."
        actions={
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <MultiSelect
              label="Dados exibidos"
              options={PANELS.filter((p) => p.id !== 'supportHours' || isAdmin).map((p) => ({ value: p.id, label: p.label }))}
              selected={visible}
              onChange={(ids) => setPanels(ids as PanelId[])}
            />
            <button className="btn btn-primary" onClick={exportPdf}>📄 Exportar PDF</button>
          </div>
        }
      />

      {/* KPIs principais coloridos */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--s4)', marginBottom: 'var(--s5)' }}>
        <Kpi label="Chamados ativos" value={String(m.totalOpen)} icon="📥" tone="info" onClick={() => goTickets({ status: TicketStatus.OPEN })} hint="Ver chamados abertos" />
        <Kpi label="Encerrados" value={String(m.totalResolved)} icon="✅" tone="success" onClick={() => goTickets({ status: TicketStatus.RESOLVED })} hint="Ver chamados resolvidos" />
        <Kpi label="SLA cumprido" value={`${m.slaResolutionRate}%`} icon="⏱" tone={m.slaResolutionRate < 80 ? 'danger' : 'success'} />
        <Kpi label="Tempo médio 1ª resposta" value={fmtMin(m.avgFirstResponseMinutes)} icon="⚡" tone="brand" />
        <Kpi label="Tempo médio resolução" value={fmtMin(m.avgResolutionMinutes)} icon="🏁" tone="purple" />
      </div>

      {visible.length === 0 ? (
        <div className="card empty">
          <div className="empty-icon">📊</div>
          Nenhum dado selecionado. Use o filtro <strong>“Dados exibidos”</strong> para escolher os gráficos.
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--s4)' }}>
          {shows('sla') && (
            <div className="card">
              <h3>SLA de resolução</h3>
              <div className="flex gap-4 mt-2" style={{ alignItems: 'center' }}>
                <Donut met={m.slaResolutionMet} breached={m.slaResolutionBreached} />
                <div className="text-sm">
                  <div><span className="badge badge-resolved">Dentro do prazo</span> {m.slaResolutionMet}</div>
                  <div className="mt-2"><span className="badge badge-urgent">Estourado</span> {m.slaResolutionBreached}</div>
                </div>
              </div>
            </div>
          )}

          {shows('status') && (
            <div className="card">
              <h3>Chamados por status</h3>
              <div className="mt-2"><BarChart data={statusData} onBarClick={(key) => goTickets({ status: key })} /></div>
            </div>
          )}

          {shows('priority') && (
            <div className="card">
              <h3>Chamados por prioridade</h3>
              {/* Filtra por prioridade dentro dos chamados abertos (status padrão da lista). */}
              <div className="mt-2"><BarChart data={priorityData} onBarClick={(key) => goTickets({ status: TicketStatus.OPEN, priority: key })} /></div>
            </div>
          )}

          {shows('ticketsByCompany') && (
            <div className="card">
              <h3>Chamados por empresa</h3>
              <div className="mt-2"><BarChart data={withPalette(m.ticketsByCompany)} emptyText="Sem chamados associados a empresas" /></div>
            </div>
          )}

          {shows('backlog') && (
            <div className="card">
              <h3>Backlog por categoria</h3>
              <div className="mt-2"><BarChart data={withPalette(m.backlogByCategory)} emptyText="Sem chamados ativos categorizados" /></div>
            </div>
          )}

          {shows('technicians') && (
            <div className="card">
              <h3>Ranking de técnicos (encerrados)</h3>
              <div className="mt-2"><BarChart data={withPalette(m.technicianRanking)} emptyText="Sem chamados encerrados com responsável" /></div>
            </div>
          )}

          {shows('hoursByCompany') && (
            <div className="card">
              <h3>Horas por empresa</h3>
              <div className="mt-2"><BarChart data={withPalette(m.hoursByCompany)} emptyText="Sem horas apontadas" /></div>
            </div>
          )}

          {shows('supportHours') && (
            // Admin-only: horas de suporte contratadas vs gastas (faturáveis) por empresa.
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 style={{ margin: 0 }}>Horas de suporte por empresa (contrato)</h3>
                <div className="flex items-end gap-2" style={{ flexWrap: 'wrap' }}>
                  <div className="field mb-0"><label className="label">De</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
                  <div className="field mb-0"><label className="label">Até</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
                  <button className="btn btn-sm btn-ghost" onClick={exportSupportPdf} disabled={!billing || billing.rows.length === 0}>⬇ Exportar PDF</button>
                </div>
              </div>
              <div className="mt-2">
                {billingError ? (
                  // Falha de carregamento: alerta visualmente distinto do vazio legítimo.
                  <div className="empty" style={{ color: 'var(--danger)' }}><div className="empty-icon">⚠️</div>{billingError}</div>
                ) : !billing || billing.rows.length === 0 ? (
                  <div className="empty"><div className="empty-icon">⏱</div>Sem horas de suporte no período.</div>
                ) : (
                  <table className="table">
                    <thead><tr>
                      <th>Empresa</th>
                      <th className="text-right">Horas contratadas (hh:mm)</th>
                      <th className="text-right">Horas gastas (hh:mm)</th>
                      <th className="text-right">Excedente (hh:mm)</th>
                      <th className="text-right">Valor adicional (R$)</th>
                    </tr></thead>
                    <tbody>
                      {billing.rows.map((r) => (
                        <tr key={r.companyId} style={{ background: r.overageMinutes > 0 ? 'var(--warning-soft, var(--brand-soft))' : undefined }}>
                          <td className="font-semibold">{r.companyName}</td>
                          <td className="text-right">{fmtHM(r.contractedMinutes)}</td>
                          <td className="text-right">{fmtHM(r.billableMinutes)}</td>
                          <td className="text-right">{fmtHM(r.overageMinutes)}</td>
                          <td className="text-right">{fmtMoney(r.overageAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type Tone = 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'purple';
const TONE_BG: Record<Tone, string> = {
  brand: 'var(--brand-soft)', info: 'var(--info-soft, var(--brand-soft))', success: 'var(--success-soft, var(--brand-soft))',
  warning: 'var(--warning-soft, var(--brand-soft))', danger: 'var(--danger-soft, var(--brand-soft))', purple: 'var(--purple-soft)',
};
const TONE_FG: Record<Tone, string> = {
  brand: 'var(--brand)', info: 'var(--info)', success: 'var(--success)',
  warning: 'var(--warning)', danger: 'var(--danger)', purple: 'var(--purple)',
};

function Kpi({ label, value, icon, tone, onClick, hint }: { label: string; value: string; icon: string; tone: Tone; onClick?: () => void; hint?: string }) {
  const clickable = !!onClick;
  return (
    <div
      className="stat-card"
      style={{ borderTop: `3px solid ${TONE_FG[tone]}`, cursor: clickable ? 'pointer' : undefined }}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={hint}
    >
      <span className="stat-icon" style={{ background: TONE_BG[tone], color: TONE_FG[tone] }}>{icon}</span>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {clickable && <span className="text-dim text-xs mt-1">ver detalhes →</span>}
    </div>
  );
}

// Donut CSS puro (conic-gradient) para o SLA cumprido vs estourado.
function Donut({ met, breached }: { met: number; breached: number }) {
  const total = met + breached;
  const pct = total ? Math.round((met / total) * 100) : 0;
  return (
    <div
      style={{
        width: 110, height: 110, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(var(--success) ${pct * 3.6}deg, var(--danger) 0)`,
        display: 'grid', placeItems: 'center', position: 'relative',
      }}
    >
      <div style={{ width: 74, height: 74, borderRadius: '50%', background: 'var(--surface)', display: 'grid', placeItems: 'center' }}>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{pct}%</span>
      </div>
    </div>
  );
}
